import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Seo } from "../components/Seo";

export default function Contact() {
  const { settings } = useStore();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in all required fields"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="fade-in">
      <Seo title="Contact Us" description="Visit or call Aadvan Dry Fruits in Sitapur. We deliver premium nuts and dry fruits within 15 KM." />
      <section className="bg-forest text-cream py-16 text-center">
        <span className="text-xs uppercase tracking-[0.3em] text-gold">We'd love to hear from you</span>
        <h1 className="font-serif text-5xl lg:text-6xl mt-2">Get in Touch</h1>
      </section>
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-serif text-3xl text-forest mb-6">Visit our store</h2>
          <div className="space-y-5">
            {[
              { icon: MapPin, t: "Address", d: settings.address },
              { icon: Phone, t: "Phone", d: settings.phone },
              { icon: Mail, t: "Email", d: settings.email },
              { icon: Clock, t: "Hours", d: "Mon–Sun, 9:00 AM – 9:00 PM" },
            ].map((c) => (
              <div key={c.t} className="flex gap-4">
                <div className="w-11 h-11 rounded-full bg-sand flex items-center justify-center text-forest shrink-0"><c.icon size={18} /></div>
                <div><div className="font-medium text-forest">{c.t}</div><div className="text-sm text-muted-foreground">{c.d}</div></div>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl overflow-hidden border border-border h-64">
            <iframe title="Aadvan Location" className="w-full h-full" loading="lazy"
              src="https://www.google.com/maps?q=Sitapur,Uttar+Pradesh&output=embed" />
          </div>
        </div>

        <div className="bg-sand rounded-2xl p-8">
          <h2 className="font-serif text-3xl text-forest mb-6">Send a message</h2>
          <form onSubmit={submit} className="space-y-4">
            <Input placeholder="Your Name *" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" className="rounded-lg bg-white" />
            <Input placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" className="rounded-lg bg-white" />
            <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" className="rounded-lg bg-white" />
            <textarea placeholder="Your message *" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" className="w-full rounded-lg border border-border p-3 text-sm bg-white outline-none focus:border-gold" />
            <Button type="submit" disabled={sending} data-testid="contact-submit" className="bg-forest text-cream hover:bg-forest-light rounded-full h-12 px-8 text-sm uppercase tracking-[0.15em]">
              {sending ? "Sending…" : <>Send Message <Send size={15} className="ml-2" /></>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
